import { useCallback } from "react";
import { useTranslation } from 'react-i18next';

/**
 * 专家选择与输入框处理 Hook
 * 封装专家选择、命令解析、输入框同步等操作
 */
const useExpertSelection = ({
  experts,
  selectedExpert,
  setSelectedExpert,
  isInExpertMode,
  setIsInExpertMode,
  currentExpertCommand,
  setCurrentExpertCommand,
  attachedFiles,
  setAttachedFiles,
  fileInputRef,
  checkExpertSummaryWithDebounce,
  removePendingExpert,
  getExpertCommandByDisplayName,
  getExpertDisplayNameByCommand,
}) => {
  const { i18n } = useTranslation();
  const isNonChinese = i18n.language !== 'zh-CN';

  // 获取专家的本地化显示名（非中文时优先英文，fallback 中文）
  const getLocalizedDisplayName = useCallback((expert) => {
    if (!expert) return '';
    if (isNonChinese && expert.display_name_en) {
      return expert.display_name_en.trim();
    }
    return (expert.display_name || '').trim();
  }, [isNonChinese]);

  // 解析用户输入的专家标识，返回用于发送的标准命令格式
  const parseExpertInput = useCallback((inputText) => {
    if (!inputText || !experts || !Array.isArray(experts)) return inputText;
    
    // 尝试匹配输入框中的专家标识（支持中文名、英文名、命令格式）
    // 优先匹配最长名称（支持带空格的英文名如 "User Journey Expert"）
    let bestMatch = null;
    let bestMatchLength = 0;
    
    for (const expert of experts) {
      // 检查中文名称
      const cnName = (expert.display_name || '').trim();
      if (cnName && inputText.startsWith(`@${cnName}`)) {
        if (cnName.length > bestMatchLength) {
          bestMatch = { identifier: cnName, command: expert.comd, length: cnName.length };
          bestMatchLength = cnName.length;
        }
      }
      
      // 检查英文名称
      const enName = (expert.display_name_en || '').trim();
      if (enName && inputText.startsWith(`@${enName}`)) {
        if (enName.length > bestMatchLength) {
          bestMatch = { identifier: enName, command: expert.comd, length: enName.length };
          bestMatchLength = enName.length;
        }
      }
      
      // 检查命令格式
      if (expert.comd && inputText.startsWith(`@${expert.comd}`)) {
        if (expert.comd.length > bestMatchLength) {
          bestMatch = { identifier: expert.comd, command: expert.comd, length: expert.comd.length };
          bestMatchLength = expert.comd.length;
        }
      }
    }
    
    // 如果找到匹配，替换为命令格式
    if (bestMatch) {
      const prefix = `@${bestMatch.identifier}`;
      const remainingText = inputText.substring(prefix.length);
      return `@${bestMatch.command}${remainingText}`;
    }
    
    // 如果没有匹配到任何专家名称，保持原样
    return inputText;
  }, [experts]);

  // 将专家标识转换为显示格式（非中文时优先英文名称）
  const formatExpertForDisplay = useCallback((expertIdentifier) => {
    if (!expertIdentifier) return expertIdentifier;
    
    // 如果是命令格式，尝试找到对应专家并返回本地化名称
    if (experts && Array.isArray(experts)) {
      const expert = experts.find(exp => exp.comd === expertIdentifier);
      if (expert) {
        return getLocalizedDisplayName(expert);
      }
    }
    
    // fallback: 尝试通过旧接口获取中文名称
    const displayName = getExpertDisplayNameByCommand(expertIdentifier);
    if (displayName) {
      return displayName;
    }
    
    // 如果找不到对应名称，保持原样
    return expertIdentifier;
  }, [experts, getExpertDisplayNameByCommand, getLocalizedDisplayName]);

  // 转换消息内容中的专家命令为中文名称显示
  const convertExpertCommandsToDisplayNames = useCallback((content) => {
    if (!content || typeof content !== 'string') return content;
    
    // 使用正则表达式匹配 @专家标识，替换为 @专家中文名称
    return content.replace(/@([^\s]+)/g, (match, expertIdentifier) => {
      const displayName = formatExpertForDisplay(expertIdentifier);
      const safeName = (displayName || expertIdentifier || '').trim();
      return `@${safeName}`;
    });
  }, [formatExpertForDisplay]);

  // 专家选择处理
  const handleExpertSelect = useCallback((expert) => {
    // 🗑️ 从待调用列表中移除（如果有）
    if (expert && (expert.name || expert.display_name)) {
      removePendingExpert(expert.display_name || expert.name);
    }

    const isSelectingSameExpert = selectedExpert && selectedExpert.comd === expert.comd;
    
    setSelectedExpert(isSelectingSameExpert ? null : expert);
    
    // 将专家中文名称添加到输入框中
    const textArea = document.querySelector('textarea[name="humanInput"]');
    if (textArea) {
      if (isSelectingSameExpert) {
        // 如果选择了相同的专家，则清除输入框中的专家前缀并退出专家模式
        const currentValue = textArea.value;
        // 支持清除中英文名称和命令格式的前缀
        const localizedName = getLocalizedDisplayName(expert);
        const displayNamePattern = new RegExp(`^@${(expert.display_name || '').trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*`);
        const displayNameENPattern = expert.display_name_en ? new RegExp(`^@${expert.display_name_en.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*`) : null;
        const commandPattern = new RegExp(`^@${expert.comd}\\s*`);
        let newValue = currentValue.replace(displayNamePattern, '').replace(commandPattern, '');
        if (displayNameENPattern) {
          newValue = newValue.replace(displayNameENPattern, '');
        }
        textArea.value = newValue;
        setIsInExpertMode(false);
        setCurrentExpertCommand('');
        
        // 如果退出的是文档分析助手，清空附件
        if (expert.comd === "document_query") {
          setAttachedFiles([]);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }
        
        // 发送检查专家交互总结的请求
        checkExpertSummaryWithDebounce(expert.comd);
      } else {
        // 移除任何现有的专家前缀（支持中文名称和命令格式）
        const currentValue = textArea.value;
        // 扩展正则表达式以支持中文字符
        const expertPrefixPattern = /^@[^\s]+\s*/;
        const cleanValue = currentValue.replace(expertPrefixPattern, '');
        
        // 如果之前是文档分析助手或现在不是文档分析助手，清空附件
        if ((selectedExpert && selectedExpert.comd === "document_query") || expert.comd !== "document_query") {
          setAttachedFiles([]);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }
        
        // 添加新的本地化名称前缀并进入专家模式
        const newDisplayPrefix = `@${getLocalizedDisplayName(expert)}`;
        const newCommand = `@${expert.comd}`;
        textArea.value = cleanValue ? `${newDisplayPrefix} ${cleanValue}` : `${newDisplayPrefix} `;
        setIsInExpertMode(true);
        setCurrentExpertCommand(newCommand); // 内部仍存储命令格式
      }
      textArea.focus();
    }
  }, [
    selectedExpert,
    setSelectedExpert,
    setIsInExpertMode,
    setCurrentExpertCommand,
    setAttachedFiles,
    fileInputRef,
    checkExpertSummaryWithDebounce,
    removePendingExpert,
    getLocalizedDisplayName,
  ]);

  // 专家命令选择处理
  const handleExpertCommandSelect = useCallback((command) => {
    // 在命令前不需要再加上@，因为 command 已经包含了 @
    const textArea = document.querySelector('textarea[name="humanInput"]');
    if (textArea) {
      // 检查当前输入框中是否已有相同的命令
      const currentValue = textArea.value;
      const commandWithoutAt = command.substring(1); // 移除@符号
      const currentCommand = currentValue.match(/^@(\w+)\s*/);
      
      if (currentCommand && currentCommand[1] === commandWithoutAt) {
        // 如果选择了相同的专家，则移除命令前缀
        const commandPattern = new RegExp(`^@${commandWithoutAt}\\s*`);
        textArea.value = currentValue.replace(commandPattern, '');
        setIsInExpertMode(false);
        setCurrentExpertCommand('');
        
        // 发送检查专家交互总结的请求
        checkExpertSummaryWithDebounce(commandWithoutAt);
      } else {
        // 移除任何现有的@command前缀
        const cleanValue = currentValue.replace(/^@\w+\s*/, '');
        
        // 添加新的命令并进入专家模式
        textArea.value = `${command} ${cleanValue}`;
        setIsInExpertMode(true);
        setCurrentExpertCommand(command);
      }
      textArea.focus();
    }
  }, [setIsInExpertMode, setCurrentExpertCommand, checkExpertSummaryWithDebounce]);

  // 处理输入框内容变化，检测用户是否删除了专家前缀
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    if (isInExpertMode && currentExpertCommand) {
      // 检查是否还有专家前缀（支持中文名称和命令格式）
      const expertPattern = /^@[^\s]+\s*/;
      const hasExpertPrefix = expertPattern.test(value);
      
      if (!hasExpertPrefix) {
        // 用户删除了专家前缀，退出专家模式
        const exitingExpertCommand = currentExpertCommand;
        
        // 如果退出的是文档分析助手，清空附件
        if (selectedExpert && selectedExpert.comd === "document_query") {
          setAttachedFiles([]);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }
        
        setIsInExpertMode(false);
        setCurrentExpertCommand('');
        setSelectedExpert(null);
        
        // 发送检查专家交互总结的请求（只有在没有重复的情况下才发送）
        const sent = checkExpertSummaryWithDebounce(exitingExpertCommand.substring(1));
        if (sent) {
          console.log(`handleInputChange: 已发送专家 ${exitingExpertCommand.substring(1)} 的检查请求`);
        } else {
          console.log(`handleInputChange: 跳过专家 ${exitingExpertCommand.substring(1)} 的检查请求（防重复）`);
        }
      } else {
        // 检查用户是否手动修改了专家标识
        const match = value.match(/^@([^\s]+)/);
        if (match && selectedExpert) {
          const inputExpertId = (match[1] || '').trim();
          // 如果输入的不是当前选中专家的中英文名称或命令，更新选中状态
          const matchesCurrent = inputExpertId === (selectedExpert.display_name || '').trim()
            || (selectedExpert.display_name_en && inputExpertId === selectedExpert.display_name_en.trim())
            || inputExpertId === selectedExpert.comd;
          if (!matchesCurrent) {
            // 尝试根据输入找到对应的专家（按中英文显示名或命令匹配）
            const expertByDisplayName = experts.find(exp => (exp.display_name || '').trim() === inputExpertId);
            const expertByDisplayNameEN = experts.find(exp => exp.display_name_en && exp.display_name_en.trim() === inputExpertId);
            const expertByCommand = experts.find(exp => exp.comd === inputExpertId);
            const newExpert = expertByDisplayName || expertByDisplayNameEN || expertByCommand;
            
            if (newExpert) {
              setSelectedExpert(newExpert);
              setCurrentExpertCommand(`@${newExpert.comd}`);
            } else {
              // 如果找不到对应专家，保持当前状态但更新命令
              setCurrentExpertCommand(`@${inputExpertId}`);
            }
          }
        }
      }
    }
  }, [
    experts,
    isInExpertMode,
    currentExpertCommand,
    selectedExpert,
    setIsInExpertMode,
    setCurrentExpertCommand,
    setSelectedExpert,
    setAttachedFiles,
    fileInputRef,
    checkExpertSummaryWithDebounce,
  ]);

  // 处理键盘事件，支持一次性删除专家前缀
  const createKeyDownHandler = useCallback((handleHumanInputMessage, isMessageInputEnabled, isProcessing) => {
    return (e) => {
      if (e.key === 'Backspace' && isInExpertMode && currentExpertCommand && selectedExpert) {
        const textarea = e.target;
        const cursorPosition = textarea.selectionStart;
        const value = textarea.value;
        
        // 检查光标是否在专家前缀的末尾（支持中英文名称和命令格式）
        const localizedPrefix = `@${getLocalizedDisplayName(selectedExpert)} `;
        const displayNamePrefix = `@${(selectedExpert.display_name || '').trim()} `;
        const displayNameENPrefix = selectedExpert.display_name_en ? `@${selectedExpert.display_name_en.trim()} ` : null;
        const commandPrefix = `@${selectedExpert.comd} `;
        
        let prefixToCheck = null;
        if (value.startsWith(localizedPrefix)) {
          prefixToCheck = localizedPrefix;
        } else if (value.startsWith(displayNamePrefix)) {
          prefixToCheck = displayNamePrefix;
        } else if (displayNameENPrefix && value.startsWith(displayNameENPrefix)) {
          prefixToCheck = displayNameENPrefix;
        } else if (value.startsWith(commandPrefix)) {
          prefixToCheck = commandPrefix;
        }
        
        if (prefixToCheck && cursorPosition <= prefixToCheck.length) {
          e.preventDefault();
          // 一次性删除整个专家前缀
          const newValue = value.substring(prefixToCheck.length);
          textarea.value = newValue;
          textarea.setSelectionRange(0, 0);
          
          // 退出专家模式
          const exitingExpertCommand = currentExpertCommand;
          
          // 如果退出的是文档分析助手，清空附件
          if (selectedExpert && selectedExpert.comd === "document_query") {
            setAttachedFiles([]);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }
          
          setIsInExpertMode(false);
          setCurrentExpertCommand('');
          setSelectedExpert(null);
          
          // 发送检查专家交互总结的请求
          const sent = checkExpertSummaryWithDebounce(exitingExpertCommand.substring(1));
          console.log(`handleKeyDown: ${sent ? '已发送' : '跳过'} 专家 ${exitingExpertCommand.substring(1)} 的检查请求`);
          
          // 触发input事件以确保状态同步
          const inputEvent = new Event('input', { bubbles: true });
          textarea.dispatchEvent(inputEvent);
          return;
        }
      }
      
      // 处理Enter键发送消息的逻辑
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (isMessageInputEnabled && !isProcessing) {
          const textArea = e.target;
          const value = textArea.value;
          const fakeEvent = {
            preventDefault: () => {},
            target: {
              elements: {
                humanInput: {
                  value: value
                }
              }
            }
          };
          handleHumanInputMessage(fakeEvent);
        }
      }
    };
  }, [
    isInExpertMode,
    currentExpertCommand,
    selectedExpert,
    setIsInExpertMode,
    setCurrentExpertCommand,
    setSelectedExpert,
    setAttachedFiles,
    fileInputRef,
    checkExpertSummaryWithDebounce,
    getLocalizedDisplayName,
  ]);

  return {
    parseExpertInput,
    formatExpertForDisplay,
    convertExpertCommandsToDisplayNames,
    handleExpertSelect,
    handleExpertCommandSelect,
    handleInputChange,
    createKeyDownHandler,
    getLocalizedDisplayName,
  };
};

export default useExpertSelection;
